import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  Input,
  Select,
  message,
  Popconfirm,
  Typography,
  Switch,
  Divider,
  InputNumber,
  Row,
  Col,
  ColorPicker,
  Tooltip,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  TeamOutlined,
  UserOutlined,
  SyncOutlined,
} from '@ant-design/icons';
import { adminApi } from '@/services/api';
import type { CohortRecalculationStatus } from '@/types';
import dayjs from 'dayjs';
import { CohortSpendDrawer } from './CohortSpendDrawer';
import { opensSpendFromRowClick } from './cohortSpendMath';

const { Title, Text } = Typography;
const { TextArea } = Input;

// Enums matching backend
const CohortType = {
  SPENDER_TIER: 'spender_tier',
  ENGAGEMENT: 'engagement',
  GEOGRAPHY: 'geography',
  LEVEL_BRACKET: 'level_bracket',
  ACQUISITION: 'acquisition',
  SUBSCRIPTION: 'subscription',
  CUSTOM: 'custom',
} as const;

const cohortTypeLabels: Record<string, string> = {
  spender_tier: 'Spender Tier',
  engagement: 'Engagement',
  geography: 'Geography',
  level_bracket: 'Level Bracket',
  acquisition: 'Acquisition',
  subscription: 'Subscription',
  custom: 'Custom',
};

interface CohortRules {
  spendingRange?: { min?: number; max?: number };
  purchaseCount?: { min?: number; max?: number };
  lastPurchaseDaysAgo?: { min?: number; max?: number };
  sessionCount?: { min?: number; max?: number };
  lastActiveDaysAgo?: { min?: number; max?: number };
  matchesPlayed?: { min?: number; max?: number };
  countries?: string[];
  excludeCountries?: string[];
  levelRange?: { min?: number; max?: number };
  daysSinceRegistration?: { min?: number; max?: number };
  hasActiveSubscription?: boolean;
  subscriptionTiers?: string[];
  platforms?: string[];
}

type Range = { min?: number; max?: number };

/** Range rules the form edits. Day counts are whole days elapsed (0-6 = within the last 7 days). */
const RANGE_RULES = [
  {
    key: 'spendingRange',
    label: 'Total Spending (INR)',
    prefix: '₹',
    extra:
      "The player's real, completed purchases in INR. Test purchases and coin or match-token buys don't count.",
  },
  {
    key: 'purchaseCount',
    label: 'INR Purchases',
    extra: 'Number of real, completed INR purchases.',
  },
  {
    key: 'lastPurchaseDaysAgo',
    label: 'Days Since Last Purchase',
    extra: 'Players who have never bought never match this rule.',
  },
  {
    key: 'lastActiveDaysAgo',
    label: 'Days Since Last Active',
    extra:
      'Days since the player was last seen (login or token refresh). Recalculation only checks ' +
      'players seen in the last 35 days, so a minimum above 35 never matches.',
  },
  { key: 'daysSinceRegistration', label: 'Days Since Sign-up' },
  { key: 'matchesPlayed', label: 'Matches Played' },
  { key: 'levelRange', label: 'Player Level' },
] as const;
type RangeRuleKey = (typeof RANGE_RULES)[number]['key'];

/** A cleared InputNumber gives null; drop it so an empty box means "no limit". */
const cleanRange = (range?: { min?: number | null; max?: number | null }): Range | undefined => {
  const out: Range = {};
  if (range?.min !== undefined && range.min !== null) out.min = range.min;
  if (range?.max !== undefined && range.max !== null) out.max = range.max;
  return out.min === undefined && out.max === undefined ? undefined : out;
};

const RECALC_POLL_MS = 5000;

const formatIst = (iso: string) =>
  `${new Date(iso).toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  })} IST`;

const recalcScope = (s: CohortRecalculationStatus) => s.cohortName ?? 'all cohorts';

const recalcSummary = (s: CohortRecalculationStatus) =>
  s.cohortId
    ? `${s.memberCount ?? 0} members (+${s.added ?? 0} / −${s.removed ?? 0})`
    : `${(s.processed ?? 0).toLocaleString()} players checked, +${s.added ?? 0} / −${
        s.removed ?? 0
      } memberships${s.errors ? `, ${s.errors} errors` : ''}`;

interface Cohort {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  type: (typeof CohortType)[keyof typeof CohortType];
  rules: CohortRules;
  priority: number;
  isActive: boolean;
  isDynamic: boolean;
  color: string | null;
  icon: string | null;
  memberCount: number;
  lastRecalculatedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

const CohortsPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [cohorts, setCohorts] = useState<Cohort[]>([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  // Member count clicked: the spend drawer for that cohort.
  const [spendCohort, setSpendCohort] = useState<{ id: string; name: string } | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminApi.getCohorts();
      // Backend returns { data: [...], meta: {...} }
      const data = res.data?.data || res.data?.items || res.data?.cohorts || res.data;
      setCohorts(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Failed to fetch cohorts', error);
      message.error('Failed to load cohorts');
      setCohorts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // "Recalculate now" runs in the background on the server; poll its status
  // while it runs, then reload the table once it ends.
  const [recalc, setRecalc] = useState<CohortRecalculationStatus | null>(null);
  const [starting, setStarting] = useState(false);
  // The finished run already reported (by startedAt), and the run this page
  // is waiting for, if any.
  const announced = useRef<string | null>(null);
  const watching = useRef<string | null>(null);
  // Set once this tab starts a run, so a slow first status load can't
  // overwrite it.
  const started = useRef(false);
  const running = Boolean(recalc?.running);

  useEffect(() => {
    adminApi
      .getCohortRecalculationStatus()
      .then((res) => {
        if (started.current) return;
        // A run already going is reported (and the table reloaded) when it
        // ends; an old result is not.
        if (res.data.running) watching.current = res.data.startedAt;
        else announced.current = res.data.startedAt;
        setRecalc(res.data);
      })
      .catch(() => {
        /* the status line just stays hidden */
      });
  }, []);

  useEffect(() => {
    if (!running) return undefined;
    const timer = setInterval(() => {
      adminApi
        .getCohortRecalculationStatus()
        .then((res) => {
          const status = res.data;
          setRecalc(status);
          if (status.running) {
            watching.current = status.startedAt;
            return;
          }
          const watched = watching.current;
          watching.current = null;
          if (watched && status.startedAt !== watched) {
            // The run vanished without a result (its server died before the
            // status could record it).
            fetchData();
            message.warning(
              `The recalculation started ${formatIst(watched)} stopped without recording a result; member counts may be partly updated. You can run it again.`
            );
            return;
          }
          if (announced.current === status.startedAt) return;
          announced.current = status.startedAt;
          fetchData();
          if (status.error) {
            message.error(`Recalculation of ${recalcScope(status)} failed: ${status.error}`);
          } else {
            message.success(`Recalculated ${recalcScope(status)}: ${recalcSummary(status)}`);
          }
        })
        .catch(() => {
          /* try again on the next tick */
        });
    }, RECALC_POLL_MS);
    return () => clearInterval(timer);
  }, [running, fetchData]);

  const startRecalculation = useCallback(async (cohort?: Cohort) => {
    setStarting(true);
    started.current = true;
    try {
      const res = await adminApi.startCohortRecalculation(cohort?.id);
      watching.current = res.data.startedAt;
      setRecalc(res.data);
      message.info(`Recalculating ${cohort ? cohort.name : 'all cohorts'}…`);
    } catch (error) {
      const response = (error as { response?: { status?: number; data?: { message?: string } } })
        .response;
      // 409: a run is going, or the daily cron window (03:45-04:45 IST).
      message.error(
        response?.data?.message ??
          (response?.status === 409
            ? 'A recalculation is already running'
            : 'Could not start the recalculation')
      );
      adminApi
        .getCohortRecalculationStatus()
        .then((res) => setRecalc(res.data))
        .catch(() => undefined);
    } finally {
      setStarting(false);
    }
  }, []);

  const handleSubmit = async (values: Record<string, unknown>) => {
    setSubmitting(true);
    try {
      const { ranges, platforms, color, ...otherValues } = values as {
        ranges?: Partial<Record<RangeRuleKey, { min?: number | null; max?: number | null }>>;
        platforms?: string[];
        color?: { toHexString?: () => string } | string;
      } & Record<string, unknown>;

      // Start from the rules being edited, so rules this form doesn't show
      // (e.g. countries, subscriptions) survive a save.
      const editing = cohorts.find((c) => c.id === editingId);
      const rules: CohortRules = { ...(editing?.rules ?? {}) };
      for (const { key } of RANGE_RULES) {
        const range = cleanRange(ranges?.[key]);
        if (range) rules[key] = range;
        else delete rules[key];
      }
      if (platforms && platforms.length > 0) rules.platforms = platforms;
      else delete rules.platforms;

      const colorValue =
        // The cohorts.color column holds '#rrggbb'; never send an alpha channel.
        typeof color === 'object' && color?.toHexString ? color.toHexString().slice(0, 7) : color;

      const payload = {
        ...otherValues,
        rules,
        color: colorValue,
      };

      if (editingId) {
        await adminApi.updateCohort(editingId, payload);
        message.success('Cohort updated');
      } else {
        await adminApi.createCohort(payload);
        message.success('Cohort created');
      }
      setModalVisible(false);
      form.resetFields();
      setEditingId(null);
      fetchData();
    } catch (error) {
      console.error('Failed to save cohort', error);
      message.error('Failed to save cohort');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (record: Cohort) => {
    setEditingId(record.id);
    form.setFieldsValue({
      ...record,
      ranges: Object.fromEntries(RANGE_RULES.map(({ key }) => [key, record.rules?.[key] ?? {}])),
      platforms: record.rules?.platforms,
    });
    setModalVisible(true);
  };

  const handleDelete = async (id: string) => {
    try {
      await adminApi.deleteCohort(id);
      message.success('Cohort deleted');
      fetchData();
    } catch (error) {
      console.error('Failed to delete cohort', error);
      message.error('Failed to delete cohort');
    }
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (name: string, record: Cohort) => (
        <div>
          <Space>
            {record.color && (
              <div
                style={{
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  backgroundColor: record.color,
                }}
              />
            )}
            <Tooltip title="Who is in this cohort and what they have spent">
              <Typography.Link
                strong
                onClick={() => setSpendCohort({ id: record.id, name: record.name })}
              >
                {name}
              </Typography.Link>
            </Tooltip>
          </Space>
          {record.slug && (
            <div>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {record.slug}
              </Text>
            </div>
          )}
          {record.description && (
            <div>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {record.description}
              </Text>
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      render: (type: string) => <Tag color="blue">{cohortTypeLabels[type] || type}</Tag>,
    },
    {
      title: 'Priority',
      dataIndex: 'priority',
      key: 'priority',
    },
    {
      title: 'Members',
      dataIndex: 'memberCount',
      key: 'memberCount',
      render: (count: number, record: Cohort) => (
        <Tooltip title="Who is in this cohort and what they have spent">
          <Button
            type="link"
            size="small"
            style={{ padding: 0 }}
            onClick={() => setSpendCohort({ id: record.id, name: record.name })}
          >
            <Space>
              <UserOutlined />
              {count?.toLocaleString() || 0}
            </Space>
          </Button>
        </Tooltip>
      ),
    },
    {
      title: 'Dynamic',
      dataIndex: 'isDynamic',
      key: 'isDynamic',
      render: (isDynamic: boolean) => (
        <Tag color={isDynamic ? 'blue' : 'default'}>
          {isDynamic ? 'Auto-recalculated' : 'Manual'}
        </Tag>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (active: boolean) => (
        <Tag color={active ? 'green' : 'default'}>{active ? 'Active' : 'Inactive'}</Tag>
      ),
    },
    {
      title: 'Last Updated',
      dataIndex: 'lastRecalculatedAt',
      key: 'lastRecalculatedAt',
      render: (date: string | null) =>
        date ? (
          <Space>
            <SyncOutlined />
            {dayjs(date).format('MMM D, HH:mm')}
          </Space>
        ) : (
          '-'
        ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: unknown, record: Cohort) => (
        <Space>
          {record.isDynamic && (
            <Tooltip
              title={
                record.isActive
                  ? 'Recalculate this cohort now'
                  : 'Switch the cohort on first: inactive cohorts are not kept up to date'
              }
            >
              <Button
                icon={<SyncOutlined />}
                size="small"
                disabled={!record.isActive || running || starting}
                onClick={() => void startRecalculation(record)}
              >
                Recalculate
              </Button>
            </Tooltip>
          )}
          <Button icon={<EditOutlined />} size="small" onClick={() => handleEdit(record)}>
            Edit
          </Button>
          <Popconfirm title="Delete this cohort?" onConfirm={() => handleDelete(record.id)}>
            <Button icon={<DeleteOutlined />} size="small" danger>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card
        title={
          <Space>
            <TeamOutlined />
            <Title level={4} style={{ margin: 0 }}>
              Cohorts
            </Title>
          </Space>
        }
        extra={
          <Space>
            <Popconfirm
              title="Recalculate every dynamic cohort now?"
              description="Re-checks every player seen in the last 35 days, as the 04:00 IST job does. It adds database load on one server for a few minutes."
              okText="Recalculate"
              onConfirm={() => void startRecalculation()}
              disabled={running}
            >
              <Button icon={<SyncOutlined spin={running} />} loading={starting} disabled={running}>
                {running ? 'Recalculating…' : 'Recalculate now'}
              </Button>
            </Popconfirm>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setEditingId(null);
                form.resetFields();
                setModalVisible(true);
              }}
            >
              Create Cohort
            </Button>
          </Space>
        }
      >
        {recalc?.startedAt && (
          <div style={{ marginBottom: 12 }}>
            {recalc.running ? (
              <Text type="secondary">
                Recalculating {recalcScope(recalc)}… started {formatIst(recalc.startedAt)}
                {recalc.startedBy ? ` by ${recalc.startedBy}` : ''}
              </Text>
            ) : recalc.error ? (
              <Text type="danger">
                Last manual recalculation ({recalcScope(recalc)}) failed
                {recalc.finishedAt ? ` at ${formatIst(recalc.finishedAt)}` : ''}: {recalc.error}
              </Text>
            ) : (
              <Text type="secondary">
                Last manual recalculation: {recalcScope(recalc)}
                {recalc.finishedAt ? `, ${formatIst(recalc.finishedAt)}` : ''} ·{' '}
                {recalcSummary(recalc)}
                {recalc.durationMs !== null ? ` in ${(recalc.durationMs / 1000).toFixed(1)} s` : ''}
              </Text>
            )}
          </div>
        )}
        <Table
          dataSource={cohorts}
          columns={columns}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 10 }}
          // A click anywhere on a row opens its spend drawer, except on the row's own
          // controls (Edit, Delete, Recalculate, switches, links).
          onRow={(record) => ({
            onClick: (e) => {
              if (opensSpendFromRowClick(e.target as Element | null)) {
                setSpendCohort({ id: record.id, name: record.name });
              }
            },
            style: { cursor: 'pointer' },
          })}
        />
      </Card>

      <CohortSpendDrawer cohort={spendCohort} onClose={() => setSpendCohort(null)} />

      <Modal
        title={editingId ? 'Edit Cohort' : 'Create Cohort'}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
          setEditingId(null);
        }}
        onOk={() => form.submit()}
        width={800}
        confirmLoading={submitting}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="name" label="Name" rules={[{ required: true }]}>
                <Input placeholder="Cohort name" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="slug"
                label="Slug"
                rules={[{ required: true }]}
                extra="URL-friendly identifier (e.g., 'whale', 'us-users')"
              >
                <Input placeholder="cohort-slug" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="description" label="Description">
            <TextArea rows={2} placeholder="Cohort description" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="type" label="Cohort Type" rules={[{ required: true }]}>
                <Select placeholder="Select type">
                  {Object.entries(CohortType).map(([key, value]) => (
                    <Select.Option key={value} value={value}>
                      {cohortTypeLabels[value] || key}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="priority" label="Priority" initialValue={0}>
                <InputNumber style={{ width: '100%' }} placeholder="Higher = evaluated first" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="color" label="Color">
                <ColorPicker disabledAlpha format="hex" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="isActive" label="Active" valuePropName="checked" initialValue={true}>
                <Switch />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="isDynamic"
                label="Dynamic"
                valuePropName="checked"
                initialValue={true}
                extra="Auto-recalculated by cron"
              >
                <Switch />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="icon" label="Icon Name">
                <Input placeholder="e.g., crown" />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left">Segmentation Rules</Divider>

          <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
            Define rules to automatically assign users to this cohort
          </Text>

          <Row gutter={16}>
            {RANGE_RULES.map((rule) => (
              <Col span={12} key={rule.key}>
                <Form.Item label={rule.label} extra={'extra' in rule ? rule.extra : undefined}>
                  <Space>
                    <Form.Item name={['ranges', rule.key, 'min']} noStyle>
                      <InputNumber
                        min={0}
                        prefix={'prefix' in rule ? rule.prefix : undefined}
                        placeholder="Min"
                        style={{ width: 110 }}
                      />
                    </Form.Item>
                    <span>to</span>
                    <Form.Item name={['ranges', rule.key, 'max']} noStyle>
                      <InputNumber
                        min={0}
                        prefix={'prefix' in rule ? rule.prefix : undefined}
                        placeholder="Max"
                        style={{ width: 110 }}
                      />
                    </Form.Item>
                  </Space>
                </Form.Item>
              </Col>
            ))}
            <Col span={12}>
              <Form.Item name="platforms" label="Platform" extra="Leave empty for all platforms.">
                <Select
                  mode="multiple"
                  allowClear
                  placeholder="All platforms"
                  options={[
                    { label: 'Android', value: 'android' },
                    { label: 'iOS', value: 'ios' },
                  ]}
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
};

export default CohortsPage;
